import { useContext, useEffect, useMemo, useState } from "react";
import IOrder from "@models/IOrder";
import IOrder_Progress_State from "@models/IOrder_Progress_State";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import { SocketContext } from "@contexts/SocketContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import EndpointsEnum from "@models/EndpointsEnum";
import IErrorDTO from "@models/DTO/IErrorDTO";
import ICreateAChargeDTO from "@models/DTO/ICreateAChargeDTO";
import IOnCreateAChargeDTO from "@models/DTO/IOnCreateAChargeDTO";
import { QuickBooksPaymentSetupDetail } from "@components/shared/QuickBooksPaymentModal/QuickBooksPaymentSetupDetail/QuickBooksPaymentSetupDetail";
import "./QuickBooksPaymentModal.scss";
import OwButton from "@components/shared/OwButton/OwButton";
import { QbPaymentContext } from "@contexts/QbPaymentContext";
import { LoadingResults } from "@components/shared/LoadingResults/LoadingResults";
import { QuickBooksPaymentProcessingDetail } from "@components/shared/QuickBooksPaymentModal/QuickBooksPaymentProcessingDetail/QuickBooksPaymentProcessingDetail";
import { QuickBooksPaymentConfirmationDetail } from "@components/shared/QuickBooksPaymentModal/QuickBooksPaymentConfirmationDetail/QuickBooksPaymentConfirmationDetail";

type Props = {
    orderModel: IOrder;
    cancelFn: (refresh?: boolean) => void;
    confirmFn: (refresh?: boolean) => void;
    nextStep?: IOrder_Progress_State;
};

export const QuickBooksPaymentModal = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const [slide, setSlide] = useState(0);

    const [isChargeDisabled, setIsChargeDisabled] = useState(!props.orderModel.qbo_CardToUseDetail);

    const [isLoading, setIsLoading] = useState(false);

    const [isProcessing, setIsProcessing] = useState(false);

    const [doneProcessing, setDoneProcessing] = useState(false);
    const [updatedCardToUse, setUpdatedCartToUse] = useState(false);

    const [existingCard, setExistingCard] = useState(props.orderModel.qbo_CardToUseDetail);

    const onlyCollectCcToUse = useMemo(() => {
        return !!props.nextStep?.onlyCollectCcToUse;
    }, []);

    const notify = (data: IErrorDTO) => {
        if (data.title === "ERROR_CREATING_A_CHARGE") {
            setIsProcessing(false);
        }
    };

    const onCreateACharge = (data: IOnCreateAChargeDTO) => {
        setDoneProcessing(true);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PaymentsOnCreateACharge, fn: onCreateACharge },
        { endpoint: EndpointsEnum.Notify, fn: notify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const createCharge = (tokenOrId: string, storedCard: boolean) => {
        const payload: ICreateAChargeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: props.orderModel.Vendor!.guid,
            orderGuids: [props.orderModel.guid],
            tokenOrId,
            storedCard,
        };

        socket.emit(EndpointsEnum.PaymentsCreateACharge, payload);

        setIsProcessing(true);
    };

    const refreshContext = () => {};

    const nextSlide = () => {
        if (slide < 2) {
            // Only go to slide 2 max (since we have 3 slides)
            setSlide(slide + 1);
        }
    };

    const prevSlide = () => {
        if (slide > 0) {
            // Only go to slide 0 min
            setSlide(slide - 1);
        }
    };

    return (
        <div className={"QuickBooksPaymentModal h-100"}>
            <QbPaymentContext.Provider value={{ existingCard, setExistingCard, isLoading, setIsLoading, nextSlide, prevSlide }}>
                {isLoading ? (
                    <div className={"cards-loading"}>
                        <LoadingResults />
                    </div>
                ) : null}

                <div className={"text-end"}>
                    <OwButton
                        centerIconClass={"fas fa-times"}
                        className={"btn-danger"}
                        fn={() => {
                            props.cancelFn();
                        }}
                    />
                </div>

                {uiContext.showHiddenFeatures ? (
                    <div>
                        <OwButton
                            title={"prev"}
                            fn={() => {
                                prevSlide();
                            }}
                            disabled={slide === 0}
                            className={"btn-primary me-2"}
                        />
                        <OwButton
                            title={"next"}
                            fn={() => {
                                nextSlide();
                            }}
                            disabled={slide === 2}
                        />
                    </div>
                ) : null}

                <div className={`slides-container slide-${slide}`}>
                    <div className={"payment-slide h-100"}>
                        <QuickBooksPaymentSetupDetail orderModel={props.orderModel} nextStep={props.nextStep} />
                    </div>

                    {onlyCollectCcToUse ? null : (
                        <div className="payment-slide h-100">
                            <QuickBooksPaymentProcessingDetail orderModel={props.orderModel} />
                        </div>
                    )}

                    <div className="payment-slide h-100">
                        <QuickBooksPaymentConfirmationDetail
                            onlyCollectCcToUse={onlyCollectCcToUse}
                            orderModel={props.orderModel}
                            confirmFn={props.confirmFn}
                        />
                    </div>
                </div>
            </QbPaymentContext.Provider>
        </div>
    );
};
